/**
 * A/B Viewer Component
 * Side-by-side comparison of Raw vs Fabricated models
 * Critical for judges to understand the value in 10 seconds
 */

import { useFabStore } from '../store';
import type { ViewMode } from '../types';

const VIEW_MODES: { id: ViewMode; label: string; icon: string }[] = [
  { id: 'raw', label: 'Raw', icon: '📦' },
  { id: 'fabricated', label: 'Fabricated', icon: '✨' },
  { id: 'split', label: 'Split', icon: '⚡' },
];

export function ABViewer() {
  const {
    viewMode,
    setViewMode,
    rawModelUrl,
    fabricatedModelUrl,
    isProcessing,
  } = useFabStore();

  const renderViewport = (type: 'raw' | 'fabricated', label: string) => {
    const url = type === 'raw' ? rawModelUrl : fabricatedModelUrl;
    const hasModel = !!url;

    return (
      <div className="flex-1 flex flex-col bg-slate-900 rounded-lg overflow-hidden border border-slate-700">
        {/* Viewport Header */}
        <div className="px-4 py-2 bg-slate-800 border-b border-slate-700 flex items-center justify-between">
          <span className="text-sm font-medium text-slate-300">
            {type === 'raw' ? '📦' : '✨'} {label}
          </span>
          {type === 'fabricated' && isProcessing && (
            <span className="text-xs text-fab-warning animate-pulse">
              Processing...
            </span>
          )}
        </div>

        {/* Viewport Content */}
        <div className="flex-1 relative">
          {hasModel ? (
            <iframe
              src={`/viewer.html?url=${encodeURIComponent(url)}&mode=${type}`}
              className="w-full h-full border-0"
              title={`${label} Viewer`}
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500">
              <span className="text-4xl mb-2">
                {type === 'raw' ? '📁' : '⏳'}
              </span>
              <p className="text-sm">
                {type === 'raw'
                  ? 'Upload a model to preview'
                  : 'Fabricate to see optimized result'
                }
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col p-4 gap-4">
      {/* View Mode Toggle */}
      <div className="flex items-center justify-center gap-2">
        {VIEW_MODES.map((mode) => (
          <button
            key={mode.id}
            onClick={() => setViewMode(mode.id)}
            disabled={mode.id === 'fabricated' && !fabricatedModelUrl}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium transition-all
              flex items-center gap-2
              ${viewMode === mode.id
                ? 'bg-fab-primary text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
              }
              ${mode.id === 'fabricated' && !fabricatedModelUrl
                ? 'opacity-50 cursor-not-allowed'
                : ''
              }
            `}
          >
            <span>{mode.icon}</span>
            {mode.label}
          </button>
        ))}
      </div>

      {/* Viewports */}
      <div className="flex-1 flex gap-4">
        {viewMode === 'raw' && renderViewport('raw', 'Raw Input')}
        {viewMode === 'fabricated' && renderViewport('fabricated', 'Fabricated Output')}
        {viewMode === 'split' && (
          <>
            {renderViewport('raw', 'Raw Input')}
            {renderViewport('fabricated', 'Fabricated Output')}
          </>
        )}
      </div>
    </div>
  );
}
